// Canonical -> legacy service-template READ adapter (pure; no Firestore/React).
//
// The canonical service editor (5A) stores canonical-shaped docs
// (recurrence:{type,weekday/dayOfMonth/date}, startTime/finishTime, derived
// durationMinutes) in organizations/{org}/serviceTemplates. The occurrence
// engine (normalizeServiceTemplate/getNextServiceOccurrences) reads the legacy
// shape. This adapter translates a canonical template into that legacy shape so
// the single subcollection-first read path can consume canonical docs.
//
// (Relocated in 5D from the removed serviceTemplateMirror.js write-mirror.)

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
// Recurring templates need a startDate <= any future occurrence so the read
// engine's "date >= startDate" gate always passes (interval defaults to 1).
const BASELINE_START_DATE = "2020-01-01";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function splitHHMM(value) {
  const m = String(value || "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return { startHour: 9, startMinute: 0, startTime: "" };
  return { startHour: Number(m[1]), startMinute: Number(m[2]), startTime: `${pad2(Number(m[1]))}:${pad2(Number(m[2]))}` };
}

/**
 * Map ONE canonical template to the legacy shape getPublicServiceOccurrences
 * understands. Preserves explicit timezone, derived duration, recurrence, and
 * active/visibility intent.
 */
export function canonicalToLegacyTemplate(canonical = {}) {
  const c = canonical || {};
  const rec = c.recurrence || {};
  const { startHour, startMinute, startTime } = splitHHMM(c.startTime);
  const weekday = Number(rec.weekday) || 0;
  const type = rec.type || "weekly";
  const isActive = c.isActive !== false;
  const isPublic = c.visibility?.isPublic !== false;

  const frequency = type === "monthly" ? "monthly" : type === "one_time" ? "one_time" : "weekly";
  const daysOfWeek = type === "weekly" ? [DAY_KEYS[weekday] || "sun"] : [];
  const startDate = type === "one_time" ? String(rec.date || "") : BASELINE_START_DATE;
  const durationMinutes = Number(c.durationMinutes) || 0;
  const timeZone = String(c.timeZone || "").trim();

  return {
    id: c.id || "",
    key: c.id || "",
    title: c.name || "Service",
    name: c.name || "Service",
    isActive,
    enabled: isActive,
    startTime,
    startHour,
    startMinute,
    durationMinutes,
    timeZone,
    weekday,
    dayOfMonth: Number(rec.dayOfMonth) || 1,
    frequency,
    daysOfWeek,
    visibility: {
      isPublic,
      showOnPublicWebsite: isPublic,
      showInCheckIn: c.attendance?.allowCheckIn !== false,
      showInMemberProfile: true,
    },
    checkIn: {
      enabled: c.attendance?.allowCheckIn !== false,
      openMinutesBeforeStart: Math.max(0, Number(c.attendance?.opensMinutesBeforeStart) || 0),
      closeMinutesAfterEnd: Math.max(0, Number(c.attendance?.closesMinutesAfterEnd ?? 120)),
    },
    recurrence: {
      frequency,
      interval: 1,
      daysOfWeek,
      dayOfMonth: Number(rec.dayOfMonth) || 1,
      startDate,
      startTime,
      durationMinutes,
      timeZone,
    },
  };
}

export { DAY_KEYS };
